/**
 * Layout Setup component module.
 * @module shared/components/LayoutSetup
 *
 */

import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {
  Steps,
  Tile,
  DescriptiveText,
  Row,
  Columnizer,
  Image,
  Checkbox,
  Column,
  RadioOption,
  Icon,
} from '../../../shared';
import Reorder from './Reorder';
import {_openModal} from '../../../shared/reducers/modals';
import debug from 'debug';
import PropTypes from 'prop-types';
import history from '../../../history';
import styled from 'styled-components/macro';

const log = debug('circleblack:investor:layoutsetup');

const contextTypeMap = {
  PORTFOLIO: 'Portfolio',
  ACCOUNT: 'Account',
  GROUP: 'Group',
};

const _ReorderColumn = styled(Column)`
  overflow: initial;
`;

/**
 * Layout Setup component
 * @name LayoutSetup
 */
const LayoutSetup = props => {
  const {context, dismiss, tiles, openModal, onSave, session} = props;
  const [stepIndex, setStepIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [layoutTiles, setLayoutTiles] = useState(Object.keys(tiles.data).map(key => ({
    key,
    content: <Column width={'130px'} margin={'5px 0'}>
      <Row margin={'0 0 5px'}>
        <DescriptiveText margin={'0'} fontSize={'16px'} alignment={'left'}>{tiles.data[key].title}</DescriptiveText>
      </Row>
      <Image maxWidth={'130px'} maxHeight={'77px'} src={tiles.data[key].url} margin={'0'}/>
    </Column>,
    selected: tiles.data[key].selected || 0,
    ...tiles.data[key],
  })));
  const [layoutType, setLayoutType] = useState();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [curLayoutSize, setCurLayoutSize] = useState('xl');
  const [totalSelected, setTotalSelected] = useState(0);

  useEffect(() => history.listen(() => {
    if (history.action === 'POP') {
      dismiss();
    }
  }), []);

  const handleWindowSize = () => {
    switch (true) {
      case (window.innerWidth >= 1820):
        setCurLayoutSize('xl');
        break;
      case (window.innerWidth >= 1090):
        setCurLayoutSize('l');
        break;
      case (window.innerWidth >= 670):
        setCurLayoutSize('m');
        break;
      case (window.innerWidth >= 400):
        setCurLayoutSize('s');
        break;
      default:
        setCurLayoutSize('xs');
    }
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    handleWindowSize();
    window.addEventListener('resize', handleWindowSize);
    return function cleanup() {
      window.removeEventListener('resize', handleWindowSize);
    };
  });

  useEffect(() => {
    const totalTiles = layoutTiles.reduce((sum, cur) => sum + (+cur.selected || 0), 0);
    setTotalSelected(totalTiles);
  }, [layoutTiles]);

  function setSelectedTiles(sTiles) {
    const selectedTiles = sTiles.filter(e => !!e.selected).sort((a, b) => a.order - b.order).map((t) => t.key);
    layoutTiles.forEach(t => {
      if (selectedTiles.includes(t.key)) {
        t.order = selectedTiles.indexOf(t.key) + 1;
      } else {
        t.order = null;
      }
    });
    setLayoutTiles(layoutTiles);
  }

  const widget = (tile, index) => <Column key={tile.key + index} width={'280px'}>
    <Row width={'270px'} margin={'0 5px'}>
      <Checkbox title={tile.title}
                dataPoint={{name: tile.key, value: !!tile.selected}}
                onChange={(name, val) => {
                  layoutTiles[index].selected = val;
                  setLayoutTiles([...layoutTiles]);
                }}
                width={'270px'}/>
    </Row>
    <Image maxWidth={'270px'} maxHeight={'160px'} src={tile.url}
           action={() => {
             layoutTiles[index].selected = !layoutTiles[index].selected;
             setLayoutTiles([...layoutTiles]);
           }}/>
  </Column>;

  const layout = (layoutNum, layoutSize) => <Column key={'layout-' + layoutNum} maxWidth={(windowWidth / 3 - 60) + 'px'}>
    <Row width={'100%'}>
      <RadioOption label={'Layout ' + layoutNum}
                   dataPoint={{name: 'layoutSelection', value: layoutType === layoutNum}}
                   onChange={() => {
                     setLayoutType(layoutNum);
                   }}/>
    </Row>
    <Image maxWidth={'100%'} maxHeight={'100%'} margin={'5px 0 '}
           action={() => {
             setLayoutType(layoutNum);
           }}
           src={`https://media.circleblack.com/investor/layouts/tiles/unnumbered/${totalSelected + (layoutSize !== 'xs' ? ('_'
             + layoutNum
             + '_') : '') + layoutSize.toUpperCase()}.png`}/>
  </Column>;

  const widgetSelectionsStep = <>
    <DescriptiveText fontSize={'18px'} margin={'30px 5px'}>Select the widgets you wish to display on your dashboard. Please select at least five
      widgets. Unselected widgets will be hidden from view but can still be accessed from the More Options menu
      (…).</DescriptiveText>
    <DescriptiveText fontSize={'18px'} alignment={'left'}>Available Widgets</DescriptiveText>
    <Columnizer itemWidth={280} justifySubRow={'left'}>
      {layoutTiles && layoutTiles.map(widget)}
    </Columnizer>
  </>;

  const layoutSelectionsStep = <>
    <DescriptiveText fontSize={'18px'} margin={'30px 5px'}>Choose the widget layout you would like for your dashboard.</DescriptiveText>
    <DescriptiveText fontSize={'18px'} alignment={'left'} margin={'5px 30px'}>Available Layouts</DescriptiveText>
    <Columnizer width={'fill'} itemWidth={windowWidth / 3 - 60} justifySubRow={'space between'}>
      {[1, 2, 3].map(c => layout(c, curLayoutSize))}
    </Columnizer>
  </>;

  const assignLayoutStep = <>
    <DescriptiveText fontSize={'18px'} margin={'30px 5px'}>The numbers under Your Widgets correspond to the numbered positions in
      Your Layout. You can drag and drop widgets to reorder them to change the position they will display in.</DescriptiveText>
    <Row justifySubRow={'space around'} alignSubRow={'top'} alignItems={'top'} wrap={true}>
      <_ReorderColumn width={'fit-content'} justifySubColumn={'space between'}>
        {layoutTiles && <Reorder title={'Your Widget'}
                                 dividerType={'border'}
                                 dataSet={{
                                   data: layoutTiles.filter(i => !!i.selected)
                                     .sort((a, b) => a.order - b.order),
                                   updateData: setLayoutTiles,
                                 }}
                                 displayNumber={true}/>}
      </_ReorderColumn>
      <Column width={'fit-content'}>
        <DescriptiveText fontSize={'18px'} alignment={'left'} margin={'10px 5px'}>Your Layout</DescriptiveText>
        <Image maxWidth={windowWidth - 260 + 'px'} maxHeight={'500px'}
               src={`https://media.circleblack.com/investor/layouts/tiles/numbered/${totalSelected + (curLayoutSize !== 'xs' ? ('_'
                 + layoutType
                 + '_') : '') + curLayoutSize.toUpperCase()}.png`}/>
      </Column>
    </Row>
  </>;

  log('rendering');
  return <Tile key={`Edit ${context.type} Layout`}
               title={`Edit ${contextTypeMap[context.type]} Layout`}
               actionIcon={
                 <Icon width={'30px'}
                       height={'30px'}
                       svg={'close'}
                       action={() => openModal({
                         type: 'ConfirmModal',
                         title: 'CANCEL EDIT LAYOUT',
                         body: 'You are leaving the Edit Layout flow. If you leave at this time the changes you made to your dashboard will not be saved.',
                         button: {
                           action: () => dismiss(false),
                         },
                       })}/>}>
    <Steps height={'100%'} stepIndex={stepIndex} displayContentAreaBackground={false}
           steps={[
             {
               title: 'Select Widgets',
               content: widgetSelectionsStep,
               next: {
                 label: 'Next',
                 action: () => setStepIndex(current => current + 1),
                 disabled: totalSelected < 5,
               },
             },
             {
               title: 'Select Layout',
               content: layoutSelectionsStep,
               previous: {
                 action: () => {
                   setStepIndex(current => current - 1);
                 },
               },
               next: {
                 label: 'Confirm',
                 action: () => {
                   setSelectedTiles(layoutTiles);
                   setStepIndex(current => current + 1);
                 },
                 disabled: !layoutType,
               },
             },
             {
               title: 'Assign Widgets to Layout',
               content: assignLayoutStep,
               previous: {
                 action: () => {
                   setStepIndex(current => current - 1);
                 },
               },
               next: {
                 label: 'Save',
                 action: () => {
                   const layoutTileData = {};
                   for (const tile of layoutTiles) {
                     if (!tile.selected) {continue;}
                     layoutTileData[tile.key] = {
                       title: tile.title,
                       order: tile.order,
                       url: tile.url,
                     };
                   }
                   onSave(totalSelected + ['A', 'B', 'C'][layoutType - 1], layoutTileData);
                 },
                 disabled: !!session.runAsInvestor,
               },
             },
           ]}
           canStepBack={true}
           errorMessage={errorMessage}
           cancelAction={dismiss}
           setErrorMessage={setErrorMessage}/>
  </Tile>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modal => dispatch(_openModal(modal)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LayoutSetup);

LayoutSetup.propTypes = {
  session: PropTypes.shape({
    runAsInvestor: PropTypes.number.isRequired,
  }).isRequired,
  openModal: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  context: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  tiles: PropTypes.shape({
    data: PropTypes.object,
  }).isRequired,
  dismiss: PropTypes.func,
};
